import React, { useCallback, useEffect, useState } from "react";
import { notification } from "antd";
import { EntityTypeEnum, FetchStatusEnum, StatusCodesEnum } from "../../../../utils/enums";
import { LocalStorageService } from "../../../../base/services/local-storage.service";
import { httpService } from "../../../../base/services/httpService.service";
import https from "../../../../base/utils/http";
import DeletedItemsModal from "../../../../components/DeletedItemsComponents/DeletedItemsModal/DeletedItemsModal";
import { notifyPlanLimitRefusal } from "../../../../components/Billing/planLimits";
import { describeArchiveFailure } from "../../../../components/Archive/archiveApi";

const PAGE_NUMBER = 1;
const PAGE_SIZE = 10;

const DeletedProjectsModal = (props) => {
   const projectId = LocalStorageService.getItem("currentProjectID");
   const workspaceID = LocalStorageService.getItem("currentWorkspaceId");

   const [data, setData] = useState([]);
   const [status, setStatus] = useState(FetchStatusEnum.Idle);
   const [pagination, setPagination] = useState({
      page: 1,
      totalSize: 0
   });
   const [deletingAll, setDeletingAll] = useState(false);

   const handleRestore = async (entity) => {
      try {
         const params = new URLSearchParams({
            entityType: EntityTypeEnum.Project,
            isDeleted: false
         });
         const res = await https.put(
            `/DataCollection/${entity.id}/toggleDeleteOrRestore?${params.toString()}`
         );
         notification.open({
            type: res.data.isSuccess ? "success" : "error",
            message: res.data.isSuccess ? "Project restored successfully" : "Oops! A problem has occurred!",
            description:
               res.data.message ||
               (res.data.isSuccess
                  ? "The project has been restored."
                  : "We had a problem restoring the project, please try again.")
         });
         fetchData();
      } catch (error) {
         // Refused, the project stays in Deleted projects: over the plan's
         // limit (409), or its workspace archived or deleted (400), in the
         // server's words, as unarchiving says them.
         if (notifyPlanLimitRefusal(error)) return;
         notification.error({
            message: "Oops! A problem has occurred!",
            description: describeArchiveFailure(
               error,
               "We had a problem restoring the project, please try again."
            )
         });
      }
   };

   const handleDelete = async (entity) => {
      setStatus(FetchStatusEnum.Loading);
      await httpService.post(
         "Projects/delete",
         {
            id: entity.id
         },
         (res) => {
            notification.open({
               type: res.isSuccess ? "success" : "error",
               message: res.isSuccess ? "Project deleted successfully" : "Oops! A problem has occurred!",
               description: (
                  <div>
                     <div>{res.message || "Something went wrong! Please try again later."}</div>
                     <br />
                     <ul>
                        {res?.errors?.map((err) => (
                           <li key={err}>{err}</li>
                        ))}
                     </ul>
                  </div>
               )
            });
            fetchData();
         },
         (_error) => {
            setStatus(FetchStatusEnum.Idle);
            notification.error({
               message: "Oops! A problem has occurred!",
               description: "We had a problem deleting the project, please try again."
            });
         }
      );
   };

   const fetchData = useCallback(async () => {
      setStatus(FetchStatusEnum.Loading);

      if (!workspaceID) {
         return;
      }
      const params = new URLSearchParams({
         isDeleted: "true",
         page: pagination.page,
         pageSize: PAGE_SIZE
      });

      httpService.get(
         `/Projects/getMyProjectsInWorkspace/${workspaceID}?${params.toString()}`,
         (res) => {
            setStatus(FetchStatusEnum.Success);
            setData(res.data?.projects);
            setPagination((prevS) => ({
               ...prevS,
               totalSize: res.data?.totalSize || 0,
               page:
                  res.data?.projects?.length === 0 && res.data?.totalSize !== 0
                     ? pagination.page - 1
                     : pagination.page
            }));
         },
         (_err) => {
            setStatus(FetchStatusEnum.Error);
         }
      );
   }, [pagination.page, workspaceID]);

   useEffect(() => {
      if (!props.isModalOpen) {
         return;
      }
      fetchData();
   }, [props.isModalOpen, fetchData]);

   const handleDeleteAll = async () => {
      try {
         setDeletingAll(true);
         const params = new URLSearchParams({
            workspaceId: workspaceID,
            projectId: projectId,
            isDeleted: true
         });
         const res = await https.post(
            `/DataCollection/permanentlyDeleteOrRestoreAllProjectsInWorkspace?${params.toString()}`
         );
         const isSuccess = res.data.statusCode === StatusCodesEnum.OK;

         if (isSuccess) {
            notification.success({
               type: "success",
               message: "Projects deleted successfully",
               description: "The projects have been permanently deleted."
            });
            props.handleOk?.(PAGE_NUMBER, PAGE_SIZE);
            props.setIsModalOpen(false);
         } else {
            notification.error({
               type: "error",
               message: "Oops! A problem has occurred!",
               description:
                  res.data.message || "We had a problem deleting the projects, please try again."
            });
         }
         setDeletingAll(false);
      } catch (_err) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description:
               _err.response?.data?.errors?.[0] ||
               "We had a problem deleting the projects, please try again."
         });
         setDeletingAll(false);
      }
   };

   const handleRestoreAll = async () => {
      try {
         setDeletingAll(true);
         const params = new URLSearchParams({
            workspaceId: workspaceID,
            isDeleted: true,
            restoreAll: true
         });
         // The route is the Projects controller's; DataCollection has none by
         // this name and answered 404. Restoring them all is refused, nothing
         // restored, when they don't fit the plan (409 PLAN_LIMIT_EXCEEDED,
         // below).
         const res = await https.post(
            `/Projects/permanentlyDeleteOrRestoreAllProjectsInWorkspace?${params.toString()}`
         );
         const isSuccess = res.data.statusCode === StatusCodesEnum.OK;
         if (isSuccess) {
            notification.success({
               type: "success",
               message: "Projects restored successfully",
               description: "The projects have been restored."
            });
            props.setIsModalOpen(false);
            props.handleOk?.(PAGE_NUMBER, PAGE_SIZE);
         } else {
            notification.error({
               type: "error",
               message: "Oops! A problem has occurred!",
               description:
                  res.data.message || "We had a problem restoring the projects, please try again."
            });
         }
         setDeletingAll(false);
      } catch (err) {
         if (!notifyPlanLimitRefusal(err)) {
            notification.error({
               message: "Oops! A problem has occurred!",
               description:
                  err.response?.data?.errors?.[0] ||
                  "We had a problem restoring the projects, please try again."
            });
         }
         setDeletingAll(false);
      }
   };

   const handleCancel = () => {
      props.handleCancel?.();
      props.setIsModalOpen(false);
   };

   const columns = [
      {
         title: "Project Name",
         dataIndex: "name",
         key: "name"
      },
      {
         title: "Manager",
         dataIndex: "manager",
         key: "manager",
         render: (manager) => manager?.fullName ?? ""
      }
   ];

   return (
      <DeletedItemsModal
         label="projects"
         isModalOpen={props.isModalOpen}
         handleCancel={handleCancel}
         entityType={EntityTypeEnum.Project}
         columns={columns}
         data={data}
         status={status}
         deletingAll={deletingAll}
         fetchData={fetchData}
         handleDelete={handleDelete}
         handleRestore={handleRestore}
         handleDeleteAll={handleDeleteAll}
         handleRestoreAll={handleRestoreAll}
         pagination={pagination}
         setPagination={setPagination}
      />
   );
};

export default DeletedProjectsModal;
